import { inject, Injectable } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import { ConfigService } from '../config/config.service';
import { ApiService } from '../apiservice/apiservice.service';
import { Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LOGIN_STATE, SharedService } from '../shared/shared.service';
import { catchError, map, switchMap, finalize } from 'rxjs/operators';
import { NotifyService } from './../../components/side-nav/notify/notify.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import * as $ from 'jquery';
import { Definitions } from '../../core/definitions';
import { TokenService } from '../token/token.service';
import { RoutingService } from '../routing/routing.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { ApiResponse } from '../api-response.interface';
import { OpacSettingsService } from '../opac-settings/opac-settings.service';
import { CatPermissions } from '../../pages/staff/users/user.service';
import { ItemsFromOtherLibraries } from '../../pages/item-details/item-details.service';

export interface UserSessionResponse {
  authStatus: number;
  jwt: JsonWebToken;
  userData: UserData;
}

export interface JsonWebToken {
  access_Token: string;
  expire_In_Seconds: number;
  refreshToken: string;
}

export interface SelfRegUserResponse {
  statusId: number;
  msgTitle: string;
  msgDetails: string;
  expiredMessage: string;
}

export interface AGAPIDropDown {
  name: string;
  value: string;
  selected: boolean;
}

export interface RegisterInitResponseModel {
  locations: AGAPIDropDown[];
  countries: AGAPIDropDown[];
  introMessage: string;
  isPasswordNumeric: boolean;
}

export interface UserData {
  userName: string;
  userId: number;
  firstName: string;
  lastName: string;
  libraryName: string;
  customerId: string;
  cpk: number;
  libraryId: string;
  lpk: number;
  expireDate: string;
  systemCategory: number;
  gaTrackingIds: string[];
  isGuest: boolean;
  isPatron: boolean;
  isStaff: boolean;
  isCustSuperUser: boolean;
  isLibSuperUser: boolean;
  isRegionalSuperUser: boolean;
  isBlocked: boolean;
  isIpAuthenticated: boolean;
  isNovelistEnabled: boolean;
  isNoPwLogin: boolean;
  isInstitution: boolean;
  sessionId: string;
  hasSlims: boolean;
  gipRedirectUrl?: unknown;
  needsPasswordChange: boolean;
  showAddToDownloadCart: boolean;
  enableKidsCat: boolean;
  allowSelfRegistration: boolean;
  allowLibraryShippingLabel: boolean;
  selfRegistrationLabel: string;
  agServiceBaseUrl: string;
  illPermissions: IIllPermissions;
  catPermissions: CatPermissions;
  illOptions: IIllOptions;
  patronBlockInfo: any;
  listPermissions: IListPermissions;
  opacOptions: IOpacOptions;
  circOption: ICircOptions;
  libraryOptions: LibraryOptions;
  patronLookupToolInfo: IPatronLookupToolInfo;
  requestableFormats: IRequestableFormats;
  showStatusColumn: boolean;
  agentSessionInfo: IAgentSessionInfo;
  guestListSaved: boolean;
  syndeticsUnboundId: string;
  usesOwnedByForILL: boolean;
  showDigitalLibraryCard: boolean;
  redirectPatron2HomeLibrary: boolean;
}

export interface IllGuestInstructions {
  loginInstructions: string;
  usernameLabel: string;
  passwordLabel: string;
}

export interface IIllPermissions {
  pRequestToBorrow: boolean;
  pRequestForCopy: boolean;
  pPatronReqTracking: boolean;
  pOrigReqForInfo: boolean;
  pAutoApprove: boolean;
  pApproveRequest: boolean;
  pViewOwnRequests: boolean;
  pCancelOrChangeRequests: boolean;
  pRespondToRequest: boolean;
  pChangeOwnILLInfo: boolean;
  pChangeAnyILLInfo: boolean;
  pEditHoldMethod: boolean;
  pChangePickupLocation: boolean;
  pAllowUndoShipped: boolean;
}

export interface IIllOptions {
  removeBlankILL: string;
  unmediatedPatronILL: string;
  usesILLReqLimits: string;
  enableSSS: string;
  allowNeedByDateChange: boolean;
  allowPickupLocationChange: boolean;
  allowPatronRequestTracking: boolean;
  showBlankILL: boolean;
  blankILLRequireLogin: boolean;
  showRequestThis: boolean;
  requestThisRequireLogin: boolean;
  allowBatchILL: boolean;
  hasILL: boolean;
  hasVersoCILL: boolean;
  showGuestTracking: boolean;
  showMultiCopy: boolean;
  accessILLRequest: boolean;
  showAvailability: boolean;
}

export interface IAgentSessionInfo {
  agentSessionId: unknown;
  agentToken: unknown;
  agentBaseUrl: unknown;
}
export interface LibraryOptions {
  blockReserveIfNotOwned: boolean;
  patronPasswordNumeric: boolean;
}
export interface IListPermissions {
  showListGuest: boolean;
  showListPatron: boolean;
  showListStaff: boolean;
}

export interface IOpacOptions {
  simpleSearchOptions: ISimpleSearchOptions;
  searchResultOptions: ISearchResultOptions;
  defaultLandingPage: string;
  enableSearch: boolean;
  showWebLinkOnSeparateTab: boolean;
  showDisplayLibraryForLogin: boolean;
}

export interface ISimpleSearchOptions {
  showIndexSelection: boolean;
  showResourceSelection: boolean;
  showTypeAhead: boolean;
  defaultScopingLevel: number;
}

export interface IRequestableFormats {
  per: boolean;
  ese: boolean;
  ser: boolean;
  enw: boolean;
  ebk: boolean;
  mic: boolean;
  lpt: boolean;
  emp: boolean;
  emu: boolean;
  eab: boolean;
  evd: boolean;
  brl: boolean;
  ejr: boolean;
  erd: boolean;
  bks: boolean;
  web: boolean;
  art: boolean;
  new: boolean;
  pmu: boolean;
  atl: boolean;
  glb: boolean;
  pcm: boolean;
  mss: boolean;
  dvd: boolean;
  blu: boolean;
  vid: boolean;
  mot: boolean;
  fst: boolean;
  sld: boolean;
  pgr: boolean;
  mcs: boolean;
  mcd: boolean;
  mlp: boolean;
  mpl: boolean;
  msr: boolean;
  acd: boolean;
  alp: boolean;
  acs: boolean;
  apl: boolean;
  nsr: boolean;
  cgm: boolean;
  onl: boolean;
  cpg: boolean;
  elr: boolean;
  fcd: boolean;
  pic: boolean;
  ngr: boolean;
  kit: boolean;
  frt: boolean;
  gme: boolean;
  toy: boolean;
  article: boolean;
  unk: boolean;
}

export interface ISearchResultOptions {
  viewOption: string;
  showPrint: boolean;
  showJacketArtOnListView: boolean;
  groupOption: string;
  sortBy: string;
  orderBy: string;
  showResultTypes: boolean;
  enableSearchHightlight: boolean;
  showSeeMoreFrom: boolean;
}

export interface ICircOptions {
  allowCheckInOut: boolean;
  isSelfReserveMediated: boolean;
  allowSelfReserve: boolean;
  allowPatronDeleteReserve: boolean;
  allowPatronReserveNote: boolean;
  allowStaffReserveNote: boolean;
  allowAudibleWarning: boolean;
  soundUrlWarning: string;
  soundUrlScanComplete: string;
  soundUrlBarcodeNotFound: string;
  soundUrlItemWarning: string;
  displayReceiptWidthChoice: boolean;
  defaultUserPassword: string;
  allowReserveAnyItem: boolean;
  allowBarcodeEditOnItemStatus: boolean;
  isPatronShared: boolean;
  statOnlyMTypeIDs: Object[];
  ibcTemplateConstDigits: string;
  ibcTemplateTotalLength: number;
  pbcTemplateConstDigits: string;
  pbcTemplateTotalLength: number;
  isCircPolicyByTranLib: boolean;
  allowItemLevelReserve: boolean;
  allowSelfRenew: boolean;
  patronExpirationWarningPeriod: number;
}

export interface IPatronLookupToolInfo {
  showPatronLookupTool: boolean;
  patronLookupToolParticipant: boolean;
  patronLookupToolRequiresPin: boolean;
}

export enum SYSTEMCATEGORY {
  VERSO = 1,
  SHAREIT = 2,
}

export enum UserType {
  Guest = 1,
  Patron = 2,
  Staff = 3,
  LSU = 4,
  CSU = 5,
}

export interface LoginRequest {
  grant_Type: string;
  cid: string;
  lid: string;
  userName: string;
  password: string;
  rememberMe: boolean;
  terminateSession?: boolean;
  guestToken?: boolean;
  gipLogin?: boolean;
  dataId?: string;
  sessionId?: string;
}

export interface SamlRequest {
  samlData: string;
  samlKey: string;
}

export interface SamlResponse {
  jwt: JsonWebToken;
  logoutRedirectUrl: string;
}

export interface EzProxyRequest {
  cid: string;
  lid: string;
  token: string;
}

export interface EzProxyResponse {
  jwt: JsonWebToken;
  logoutRedirectUrl: string;
}

@Injectable()
export class AuthService extends Definitions {
  //TODO - look at usage and refactor out
  static USER_NO_CILL_PERMISSION = 'Please contact library staff about this request.';
  private _ezKey = '_ez';
  private _4rmezKey = '_4rm_ez';
  private _samlLoginKey = '_saml_login';
  private _isLoggedIn: boolean;
  private _apiService = inject(ApiService);
  private _configService = inject(ConfigService);
  private _http = inject(HttpClient);
  private _notifyService = inject(NotifyService);
  private _routingService = inject(RoutingService);
  private _snackBar = inject(MatSnackBar);
  private _sharedService = inject(SharedService);
  private _storageService = inject(StorageService);
  private _tokenService = inject(TokenService);
  private _analyticsService = inject(AnalyticsService);
  private _opacService = inject(OpacSettingsService);

  getUserDataFromSession() {
    return this._storageService.getUserData();
  }

  //TODO Remove
  getIllOptionsCached() {
    const userdata = this.getUserDataFromSession();
    if (userdata) {
      return userdata.illOptions;
    }
    return null;
  }

  //TODO Remove
  getIllPermissionsCached() {
    const userdata = this.getUserDataFromSession();
    if (userdata) {
      return userdata.illPermissions;
    }
    return null;
  }

  //TODO Remove
  getLibraryOptionsCached() {
    const userdata = this.getUserDataFromSession();
    if (userdata) {
      return userdata.libraryOptions;
    }
    return null;
  }

  //TODO Remove
  getListPermissionsCached() {
    const userdata = this.getUserDataFromSession();
    if (userdata) {
      return userdata.listPermissions;
    }
    return null;
  }

  //TODO Remove
  getOpacOptionsCached() {
    const userdata = this.getUserDataFromSession();
    if (userdata) {
      return userdata.opacOptions;
    }
    return null;
  }

  //TODO Remove
  getCircOptionsCached() {
    const userdata = this.getUserDataFromSession();
    if (userdata) {
      return userdata.circOption;
    }
    return null;
  }

  //TODO Remove
  getPatronLookupToolInfoCached() {
    const userdata = this.getUserDataFromSession();
    if (userdata) {
      return userdata.patronLookupToolInfo;
    }
    return null;
  }

  //TODO Remove
  getRequestableFormatsCached() {
    const userdata = this.getUserDataFromSession();
    if (userdata) {
      return userdata.requestableFormats;
    }
    return null;
  }

  set4rmEzProxy(value): void {
    this._storageService.setItem(this._4rmezKey, value);
  }

  get4rmEzProxy(): any {
    return this._storageService.getItem(this._4rmezKey);
  }

  private setEzProxy(value): void {
    this._storageService.setItem(this._ezKey, value);
  }

  private getEzProxy(): any {
    return this._storageService.getItem(this._ezKey);
  }

  private setSamlLogin(value): void {
    this._storageService.setItem(this._samlLoginKey, value);
  }

  private getSamlLogin(): any {
    return this._storageService.getItem(this._samlLoginKey);
  }

  getLoginRequest(uid, cid): Observable<LoginRequest> {
    const url = `${this._configService.agApi}/auth/loginRequest?uid=${uid}&cid=${cid}`;
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<LoginRequest>) => {
        if (response.status == '200 OK' && response.message.toLowerCase() == 'success') {
          return response.data;
        } else {
          throw new Error('Unable to find login request');
        }
      }),
    );
  }

  async getGuestIllReqAsync(body) {
    try {
      const url = `${this._configService.agApi}/account/GetGuestIllRequests`;
      const response: any = await this._http.post(url, body).toPromise();
      if (response) {
        if (
          response.status == '200 OK' &&
          response.message.toLowerCase() == 'success' &&
          response.data !== null
        ) {
          this._openSnackBar(response.message, 'ok');
          return response.data;
        }
      }
    } catch (error) {
      console.error(`authService:getGuestIllReqAsync => ${error.message}`);
    }
    return null;
  }

  getIllGuestInstructions(): Observable<IllGuestInstructions> {
    const url = this._configService.agApi + '/account/GetIllGuestInstructions';
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<IllGuestInstructions>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  getGuestIllReq(data): Observable<ApiResponse<ItemsFromOtherLibraries>> {
    const url = this._configService.agApi + '/account/GetGuestIllRequests';
    return this._apiService.post(url, data);
  }

  forgotPasswordWithLidAsync(request_body): Observable<any> {
    const url = `${this._configService.agApi}/email/ForgotPasswordWithLid`;
    return this._http.post(url, request_body);
  }

  forgotPassword(data): Observable<any> {
    const url = this._configService.agApi + '/email/Forgotpassword';
    return this._apiService.post(url, data);
  }

  forgotPasswordRequest(data): Observable<any> {
    const url = this._configService.agApi + '/auth/ForgotPasswordRequest';
    return this._apiService.post(url, data);
  }

  resetpassword(data): Observable<any> {
    const url = this._configService.agApi + '/auth/ForgotPasswordReset';
    return this._apiService.post(url, data);
  }

  refreshToken(token: string): Observable<string> {
    const url = `${this._configService.agApi}/auth`;

    const options = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return this._apiService
      .get<ApiResponse<JsonWebToken>>(url, options)
      .pipe(map((response) => response.data.access_Token));
  }

  updatePassword(passwordRequest): Observable<any> {
    return this._apiService.post(this._configService.agApi + '/auth/updatepw', passwordRequest);
  }

  signup(credentials): Observable<any> {
    const url = this._configService.agApi + '/users/signup';
    return this._apiService.post(url, credentials);
  }

  guestLogin(): Observable<any> {
    return this.login(this.createLoginRequest());
  }

  login(loginRequest: LoginRequest): Observable<UserData> {
    const url = this._configService.agApi + '/auth';
    return this._apiService.post(url, loginRequest).pipe(
      map((response: JsonWebToken) => {
        this._tokenService.setToken(response.access_Token);
      }),
      switchMap(() => {
        this._opacService.getLoginOptions().subscribe();
        return this.finalizeLogin();
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          return throwError(() => new Error(error.error));
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  finalizeLogin(): Observable<UserData> {
    const url = `${this._configService.agApi}/auth/userdata`;
    return this._apiService.get<UserData>(url).pipe(
      map((userData: UserData) => {
        this._storageService.setItem('isGuest', userData.isGuest);
        this._storageService.setItem(
          'blankILLRequireLogin',
          userData.illOptions?.blankILLRequireLogin,
        );

        if (userData.agentSessionInfo) {
          this._storageService.setItem(Definitions.agentInfoSessionKey, userData.agentSessionInfo);
        }

        this._sharedService.setUserData(userData);
        this._sharedService.setLibraryChangedBS(
          userData.customerId?.toLowerCase(),
          userData.libraryId?.toLowerCase(),
        );

        this._isLoggedIn = true;
        return userData;
      }),
    );
  }

  getSelfRegistration(): Observable<RegisterInitResponseModel> {
    const url = `${this._configService.agApi}/registration`;
    return this._http.get(url).pipe(
      map((response: ApiResponse<RegisterInitResponseModel>) => {
        if (response?.status == '200 OK' && response?.message.toLowerCase() == 'success') {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
      switchMap((data: RegisterInitResponseModel) => {
        return of(data);
      }),
    );
  }

  /**
   * Fetched GeoLocations by PostalCode
   * @param postalCode
   * @returns  Observable<PostalLocation>
   */
  getGeoLocations(postalCode: string): Observable<any> {
    const url = this._configService.agApi + `/registration/postalcode/${postalCode}`;
    return this._apiService.get(url);
  }

  /*
  public updateSelfRegistration(data): Observable<any> {
    const url = this.configService.agApi + `/registration/next`;
    return this.apiService.post(url, data);
  }
  */

  submitSelfRegistration(data): Observable<any> {
    const url = this._configService.agApi + '/registration';
    return this._apiService.post(url, data);
  }

  saveRM2Cache(key, value): void {
    localStorage.setItem(key.trim(), JSON.stringify(value.trim()));
  }

  getRM4rmCache(key): Object {
    if (this.isExistRMinCache(key.trim()) !== '') {
      return JSON.parse(localStorage.getItem(key.trim()));
    }
    return null;
  }

  isExistRMinCache(key): string {
    if (localStorage.getItem(key.trim()) !== null) {
      return key.trim();
    } else {
      return '';
    }
  }

  removeRememberMe4rmCache(key): void {
    if (this.isExistRMinCache(key.trim()) !== '') {
      localStorage.removeItem(key.trim());
    }
  }

  saveRememberMe2Cache(cid, lid, userName): void {
    const KEY = this._rememberMeKey(cid, lid);
    this.saveRM2Cache(KEY, userName);
  }

  _rememberMeKey(cid, lid): string {
    return '_' + cid.toLowerCase() + '_' + lid.toUpperCase();
  }

  IsAuthenticated(): boolean {
    return this._isLoggedIn;
  }

  isGuest(): boolean {
    const userDetails = this.getUserDataFromSession();
    if (!userDetails) {
      return false;
    } else {
      return userDetails.isGuest;
    }
  }

  isPatron(): boolean {
    const userDetails = this.getUserDataFromSession();
    if (!userDetails) {
      return false;
    } else {
      return userDetails.isPatron;
    }
  }

  isStaff(): boolean {
    const userDetails = this.getUserDataFromSession();
    if (!userDetails) {
      return false;
    } else {
      return userDetails.isStaff;
    }
  }

  //   -------------------------------------------------------------------------

  hasDisplayYourListPermission(): boolean {
    let isAllowed = false;
    const userDetails = this.getUserDataFromSession();
    if (userDetails) {
      const listPermissions = userDetails.listPermissions ? userDetails.listPermissions : null;
      if (listPermissions != null) {
        if (this.isStaff()) {
          isAllowed = listPermissions.showListStaff;
        } else if (this.isPatron()) {
          isAllowed = listPermissions.showListPatron;
        } else {
          isAllowed = listPermissions.showListGuest;
        }
      }
    }
    return isAllowed;
  }

  //TODO Move
  cannnotPlaceIllrequestMessage(): void {
    const message = '';
    this._openSnackBar(message, 'ok');
  }

  private _logout(): Observable<any> {
    const url = this._configService.agApi + '/auth/logout';
    return this._apiService.get(url);
  }

  OnLogOut(): void {
    $('.mat-drawer-shown').click();
    this._analyticsService.logEvent('Logout');
    this._logout()
      .pipe(finalize(() => this.cleanUpSession()))
      .subscribe({
        next: () => {
          this._sharedService.setLoginStatusBS(LOGIN_STATE.LOGOUT);
        },
        error: (error: unknown) => {
          console.log('logout error');
          console.log(error);
        },
      });
  }

  cleanUpSession() {
    const redirectURL = this._storageService.getItem('sso_Logout_Redirect_Url');
    const ezProxy = this.getEzProxy();
    const formEzProxy = this.get4rmEzProxy();
    const samlLogin = this.getSamlLogin();

    this._notifyService.stopUnreadMessagecountUpdates();
    this._storageService.clearAll();
    this._routingService.clearUrls();

    if (ezProxy) {
      if (formEzProxy) {
        window.location.href = redirectURL;
      } else {
        this.guestLogin().subscribe({
          next: (userData) => {
            this._routingService.redirectAfterLogin(userData);
          },
          error: (error: unknown) => {
            console.log(error);
          },
        });
      }
    } else if (samlLogin) {
      const origin = this._routingService.baseUri;
      const url = `${origin}${redirectURL}`;
      window.location.href = url;
    }

    this.guestLogin().subscribe(
      (userData) => {
        this._routingService.redirectAfterLogin(userData);
      },
      (error: unknown) => {
        console.log(error);
      },
    );
  }

  //
  // ------------------ Saml auth Service ----------------------------
  //

  loginSaml(data: SamlRequest): Observable<UserData> {
    const url = `${this._configService.agApi}/auth/sso/saml`;
    return this._apiService.post(url, data).pipe(
      map((response: SamlResponse) => {
        this._storageService.setItem('sso_Logout_Redirect_Url', response.logoutRedirectUrl);
        this.setSamlLogin(true);
        this._tokenService.setToken(response.jwt.access_Token);
      }),
      switchMap(() => {
        return this.finalizeLogin();
      }),
    );
  }

  loginEzProxy(data: EzProxyRequest): Observable<UserData> {
    const _url = this._configService.agApi + '/auth/sso/ezproxy';
    return this._apiService.post(_url, data).pipe(
      map((response: EzProxyResponse) => {
        this._storageService.setItem('sso_Logout_Redirect_Url', response?.logoutRedirectUrl);
        this.setEzProxy(true);
        this._tokenService.setToken(response.jwt.access_Token);
      }),
      switchMap(() => {
        return this.finalizeLogin();
      }),
    );
  }

  //TODO Stop using this method
  loginSuccess(): boolean {
    return this.isLoggedIn();
  }

  isLoggedIn(): boolean {
    const userData = this.getUserDataFromSession();
    if (userData) {
      return !this.isGuest();
    }
    return false;
  }

  currentURL(): string {
    return this._storageService.getItem('currentUrl');
  }

  createLoginRequest(userName: string = 'guest', password: string = 'guest'): LoginRequest {
    return {
      grant_Type: 'password',
      cid: this._storageService.getCid(),
      lid: this._storageService.getLid(),
      userName: userName,
      password: password,
      rememberMe: false,
    };
  }

  private _openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }
}
